/**
 * Module registration order.
 *
 * `order` on each module governs init and update sequencing; the list below is
 * only about wiring. Keep imports here and nowhere else, so the dependency graph
 * between top-level modules stays a star through `@/contracts`.
 */
import type { Engine } from '@/core/engine';

export async function registerModules(engine: Engine): Promise<void> {
  const [
    { MaterialsModule },
    { RenderModule },
    { PhysicsModule },
    { TerrainModule },
    { SandModule },
    { WorldModule },
    { VehiclesModule },
    { BuildModule },
    { GameplayModule },
    { FxModule },
    { AudioModule },
    { UiModule },
  ] = await Promise.all([
    import('@/materials'),
    import('@/render'),
    import('@/physics'),
    import('@/terrain'),
    import('@/sand'),
    import('@/world'),
    import('@/vehicles'),
    import('@/build'),
    import('@/gameplay'),
    import('@/fx'),
    import('@/audio'),
    import('@/ui'),
  ]);

  engine
    .add(new MaterialsModule())
    .add(new RenderModule())
    .add(new PhysicsModule())
    .add(new TerrainModule())
    .add(new SandModule())
    .add(new WorldModule())
    .add(new VehiclesModule())
    .add(new BuildModule())
    .add(new GameplayModule())
    .add(new FxModule())
    .add(new AudioModule())
    .add(new UiModule());
}

/**
 * Init/update order. Lower runs first.
 *
 *   10 materials  — everything else asks it for surfaces
 *   20 render     — owns lights and the sun the world is lit by
 *   30 physics    — world must exist before colliders are registered
 *   40 terrain    — registers heightfield colliders, needs physics + materials
 *   45 sand       — sand-depth layer over frozen base terrain; samples terrain
 *                   heights at init, registers its own coarse colliders, and is
 *                   read by world (curbs/props) and vehicles (scoop/pour)
 *   50 world      — set dressing on top of terrain
 *   60 vehicles   — spawns onto terrain, needs physics
 *   70 build      — structures sit on terrain, consume vehicle payloads
 *   80 gameplay   — reads build + vehicles to score contracts
 *   90 fx         — reacts to everything above
 *   95 audio      — listener follows the camera set by vehicles/render
 *  100 ui         — reads final state of the frame
 */
export const MODULE_ORDER = {
  materials: 10, render: 20, physics: 30, terrain: 40, sand: 45, world: 50,
  vehicles: 60, build: 70, gameplay: 80, fx: 90, audio: 95, ui: 100,
} as const;
